import { useEffect, useMemo, useState } from 'react'

import {
  Route,
  Switch,
  useRouteMatch,
  Redirect,
  useParams,
} from 'react-router-dom'
import styled from 'styled-components'

import { useBuildingContext } from '../../../contexts/buildingContext'
import { useUserContext } from '../../../contexts/userContext'
import {
  PATH_STRINGS,
  useGeneratedPaths,
} from '../../../hooks/useGeneratedPaths'
import { useNavigation } from '../../../hooks/useNavigation'
import { LoadingIndicator } from '../../common/LoadingIndicator'
import { PermissionRoute } from '../../common/PermissionRoute/PermissionRoute'
import { checkUserPermissions } from '../../common/PermissionWrapper/PermissionWrapper'
import { BuildingOverview } from '../building_page/components/BuildingOverview/BuildingOverview'
import { ViewModeMenu } from '../building_page/components/ViewModeMenu'
import {
  useBuildingQuery,
  useFloorImages,
  useImagesQuery,
} from '../building_page/hooks/buildingQueries'
import { ReviewAiBatchReviewPage } from '../review/ReviewAiBatchReviewPage'
import { ReviewTaskList } from '../review/ReviewTaskList'

import { FloorSelector } from './components/FloorSelector'
import { ManageEquipment } from './components/ManageEquipment/ManageEquipment'
import { ManageNoGoZones } from './components/ManageNoGoZones/ManageNoGoZones'
import { IMapPoint } from './components/ManagePoints/ManagedMapPoint'
import { ManagePoints } from './components/ManagePoints/ManagePoints'
import { ManagePointsProvider } from './components/ManagePoints/ManagePointsContext'
import { ManageSchedules } from './components/ManageSchedules/ManageSchedules'
import { ManageTargetZones } from './components/ManageTargetZones/ManageTargetZones'
import { ManageUsers } from './components/ManageUsers/ManageUsers'
import { ManageZones } from './components/ManageZones/ManageZones'
import { ManageScanHealth } from './components/ScanHealth/ManageScanHealth'
import { useFloorGroupsAndMappingsQuery } from './hooks/adminBuildingQueries'
import { useAdminRouteData } from './hooks/useAdminRouteData'

export const AdminBuildingPage = () => {
  const {
    generateManagePointsProjectPath,
    generateManageNoGoZonesProjectPath,
    generateManageZonesProjectPath,
    generateManageTargetZonesFloorPath,
    generateManageTargetZonesProjectPath,
    generateAdminProjectPath,
    generateManageEquipmentProjectPath,
    generateManageUsersProjectPath,
    generateAdminScanHealthPath,
    generateAdminManageReviewPath,
  } = useGeneratedPaths()
  const { state: userState } = useUserContext()
  const { projectId: urlProjectId } = useParams<{ projectId: string }>()

  useAdminRouteData()

  const {
    navigateToManagePointsFloor,
    navigateToManageNoGoZonesFloor,
    navigateToManageZonesFloor,
    navigateToManageTargetZonesFloor,
  } = useNavigation()

  const onClickBuildingOverviewFloorManagePoints = (floor: string) => {
    navigateToManagePointsFloor(floor)
  }

  const onClickBuildingOverviewFloorManageNoGoZones = (floor: string) => {
    navigateToManageNoGoZonesFloor(floor)
  }

  const onClickBuildingOverviewFloorManageZones = (floor: string) => {
    navigateToManageZonesFloor(floor)
  }

  const onClickBuildingOverviewFloorManageTargetZones = (floor: string) => {
    navigateToManageTargetZonesFloor(floor)
  }

  const {
    updateBuilding,
    updateFloor,
    updateImage,
    updatePoint,
    state: buildingState,
  } = useBuildingContext()

  const { data: buildingData, isLoading: buildingLoading } = useBuildingQuery(
    updateBuilding,
    { includeInactiveFloors: true },
  )

  const [hoverFloor, setHoverFloor] = useState<string | null>(null)

  const matchesManageSchedules = !!useRouteMatch(PATH_STRINGS.adminSchedules)
  const onlyLoadActivePoints: boolean = matchesManageSchedules

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper data type
  const updateFloorDesc = (data: any) => {
    updateFloor(data)
  }

  const { isLoading: floorLoading, isIdle: floorIdle } = useFloorImages(
    updateFloorDesc,
    onlyLoadActivePoints,
    undefined,
    undefined,
    undefined,
    true,
  )

  const imagesQuery = useImagesQuery(
    buildingState.dateRange,
    buildingState.imageData,
    updateImage,
    updateImage,
  )

  const { isLoading: groupsLoading } =
    useFloorGroupsAndMappingsQuery(updateFloorDesc)

  useEffect(() => {
    const point = buildingState.floorData.points?.find(
      (point: IMapPoint) => buildingState.pointId === point.point_id.toString(),
    )
    if (point && buildingState.imageData) {
      updatePoint({
        ...point,
        panoramaUrl: buildingState.imageData.image,
        panoramaThumbUrl: point.panoramaUrl,
      })
    }
    // eslint-disable-next-line
  }, [buildingState.imageData, buildingState.pointId])

  useEffect(() => {
    if (imagesQuery.isSuccess) imagesQuery.refetch()
    // eslint-disable-next-line
  }, [buildingState.dateRange])

  // Check if user is a super user (has all permissions)
  const isSuperUser = userState.user_type === 'super'

  // Super users and Project Admins see all tabs in the admin zone
  const hasFullAdminAccess = useMemo(() => {
    if (isSuperUser) return true

    return checkUserPermissions(
      userState.permissions,
      'project_tables.admin_full_access',
      urlProjectId,
    )
  }, [userState.permissions, urlProjectId, isSuperUser])

  // User Admins only see the Users tab
  const isUserAdminOnly = useMemo(() => {
    if (hasFullAdminAccess) return false

    const hasAdminZoneAccess = checkUserPermissions(
      userState.permissions,
      'project_tables.admin_zone_access',
      urlProjectId,
    )
    return hasAdminZoneAccess
  }, [userState.permissions, urlProjectId, hasFullAdminAccess])

  // Check if on default admin page (Schedule tab)
  const matchesDefaultAdminPage = !!useRouteMatch({
    path: PATH_STRINGS.adminProject,
    exact: true,
  })
  const matchesTargetZonesOverviewPage = !!useRouteMatch({
    path: PATH_STRINGS.adminManageTargetZones,
    exact: true,
  })
  const matchesScanHealth = !!useRouteMatch({
    path: PATH_STRINGS.adminScanHealth,
    exact: true,
  })

  const ADMIN_VIEW_MODE_MAP = useMemo(() => {
    const baseMap = {
      Schedule: {
        route: buildingState.projectId
          ? generateAdminProjectPath(buildingState.projectId)
          : '',
        display: true,
        userCanAccess:
          isSuperUser ||
          checkUserPermissions(
            userState.permissions,
            'scheduler_tables.read_scheduler_schedule',
            buildingState.projectId,
          ),
      },
      'No-Go Areas': {
        route: buildingState.projectId
          ? generateManageNoGoZonesProjectPath(buildingState.projectId)
          : '',
        display: true,
        userCanAccess:
          isSuperUser ||
          checkUserPermissions(
            userState.permissions,
            'project_tables.read_no_go_zone',
            buildingState.projectId,
          ),
      },
      Zones: {
        route: buildingState.projectId
          ? generateManageZonesProjectPath(buildingState.projectId)
          : '',
        display: true,
        userCanAccess:
          isSuperUser ||
          checkUserPermissions(
            userState.permissions,
            'viewpoints_tables.read_viewpoints_point',
            buildingState.projectId,
          ),
      },
      'Target Zones': {
        route: buildingState.projectId
          ? generateManageTargetZonesProjectPath(buildingState.projectId)
          : '',
        additionalActivePaths:
          buildingState.projectId && buildingState.floorId
            ? [
                generateManageTargetZonesFloorPath(
                  buildingState.projectId,
                  buildingState.floorId,
                ),
              ]
            : [],
        display: true,
        userCanAccess:
          isSuperUser ||
          checkUserPermissions(
            userState.permissions,
            'viewpoints_tables.read_viewpoints_point',
            buildingState.projectId,
          ),
      },
      Equipment: {
        route: buildingState.projectId
          ? generateManageEquipmentProjectPath(buildingState.projectId)
          : '',
        display: true,
        userCanAccess:
          isSuperUser ||
          checkUserPermissions(
            userState.permissions,
            'project_tables.read_equipment',
            buildingState.projectId,
          ),
      },
      'New Points': {
        route: buildingState.projectId
          ? generateManagePointsProjectPath(buildingState.projectId)
          : '',
        display: true,
        userCanAccess:
          isSuperUser ||
          checkUserPermissions(
            userState.permissions,
            'viewpoints_tables.read_viewpoints_point',
            buildingState.projectId,
          ),
      },
      'Scan Health': {
        route: buildingState.projectId
          ? generateAdminScanHealthPath(buildingState.projectId)
          : '',
        display: true,
        userCanAccess:
          isSuperUser ||
          checkUserPermissions(
            userState.permissions,
            'project_tables.admin_zone_access',
            buildingState.projectId,
          ),
      },
      Users: {
        route: buildingState.projectId
          ? generateManageUsersProjectPath(buildingState.projectId)
          : '',
        display: true,
        userCanAccess:
          isSuperUser ||
          checkUserPermissions(
            userState.permissions,
            'project_tables.read_project',
            buildingState.projectId,
          ),
      },
      Review: {
        route: buildingState.projectId
          ? generateAdminManageReviewPath(buildingState.projectId)
          : '',
        display: true,
        userCanAccess: isSuperUser,
        badge: buildingState.projectData.pending_review_task_count || undefined,
      },
    }

    // User Admins see Scan Health and Users tabs only
    if (isUserAdminOnly) {
      return {
        'Scan Health': baseMap['Scan Health'],
        Users: baseMap.Users,
      }
    }

    return baseMap
  }, [
    buildingState.floorId,
    buildingState.projectId,
    generateAdminProjectPath,
    generateManageEquipmentProjectPath,
    generateManageNoGoZonesProjectPath,
    generateManageZonesProjectPath,
    generateManageTargetZonesFloorPath,
    generateManageTargetZonesProjectPath,
    generateManagePointsProjectPath,
    generateAdminScanHealthPath,
    generateManageUsersProjectPath,
    generateAdminManageReviewPath,
    userState.permissions,
    isUserAdminOnly,
    isSuperUser,
    buildingState.projectData.pending_review_task_count,
  ])

  if (buildingLoading || !buildingData) {
    return <LoadingIndicator />
  }

  // Redirect User Admin only users to Users tab when landing on default admin page
  if (isUserAdminOnly && matchesDefaultAdminPage && urlProjectId) {
    return <Redirect to={generateManageUsersProjectPath(urlProjectId)} />
  }

  // Target zones are floor-scoped; route directly into a floor editor.
  if (
    matchesTargetZonesOverviewPage &&
    urlProjectId &&
    buildingData?.floors?.length
  ) {
    const fallbackFloorCode = buildingData.floors[0].floor_code
    const targetFloorCode = buildingState.floorId || fallbackFloorCode
    return (
      <Redirect
        to={generateManageTargetZonesFloorPath(urlProjectId, targetFloorCode)}
      />
    )
  }

  return (
    <AdminPageRoot $isScanHealth={matchesScanHealth}>
      <FloorSelector hoverFloor={hoverFloor} setHoverFloor={setHoverFloor} />

      <Switch>
        <Route
          path={[
            PATH_STRINGS.adminProject,
            PATH_STRINGS.adminManagePoints,
            PATH_STRINGS.adminManageNoGoZones,
            PATH_STRINGS.adminManageZones,
            PATH_STRINGS.adminManageTargetZones,
            PATH_STRINGS.adminManageTargetZonesFloor,
            PATH_STRINGS.adminManageEquipment,
            PATH_STRINGS.adminManageUsers,
            PATH_STRINGS.adminScanHealth,
            PATH_STRINGS.adminManageReview,
          ]}
          exact
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '25px',
              marginTop: '38px',
            }}
          >
            <ViewModeMenu items={ADMIN_VIEW_MODE_MAP} />
          </div>
        </Route>
      </Switch>

      <AdminContentArea $isScanHealth={matchesScanHealth}>
        <Switch>
          <PermissionRoute
            exact
            permission="viewpoints_tables.read_viewpoints_point"
            path={PATH_STRINGS.adminManagePoints}
            component={BuildingOverview}
            hoverFloor={hoverFloor}
            setHoverFloor={setHoverFloor}
            onFloorClicked={onClickBuildingOverviewFloorManagePoints}
          />
          <PermissionRoute
            permission="viewpoints_tables.read_viewpoints_point"
            path={PATH_STRINGS.adminManagePointsFloor}
            component={
              floorLoading || floorIdle || groupsLoading
                ? LoadingIndicator
                : WrappedManagePoints
            }
          />
          <PermissionRoute
            exact
            permission="project_tables.read_no_go_zone"
            path={PATH_STRINGS.adminManageNoGoZones}
            component={BuildingOverview}
            hoverFloor={hoverFloor}
            setHoverFloor={setHoverFloor}
            onFloorClicked={onClickBuildingOverviewFloorManageNoGoZones}
          />
          <PermissionRoute
            permission="project_tables.read_no_go_zone"
            path={PATH_STRINGS.adminManageNoGoZonesFloor}
            component={ManageNoGoZones}
            floorLoading={floorLoading || floorIdle}
          />
          <PermissionRoute
            exact
            permission="viewpoints_tables.read_viewpoints_point"
            path={PATH_STRINGS.adminManageZones}
            component={BuildingOverview}
            hoverFloor={hoverFloor}
            setHoverFloor={setHoverFloor}
            onFloorClicked={onClickBuildingOverviewFloorManageZones}
          />
          <PermissionRoute
            permission="viewpoints_tables.read_viewpoints_point"
            path={PATH_STRINGS.adminManageZonesFloor}
            component={
              floorLoading || floorIdle || groupsLoading
                ? LoadingIndicator
                : ManageZones
            }
          />
          <PermissionRoute
            exact
            permission="viewpoints_tables.read_viewpoints_point"
            path={PATH_STRINGS.adminManageTargetZones}
            component={BuildingOverview}
            hoverFloor={hoverFloor}
            setHoverFloor={setHoverFloor}
            onFloorClicked={onClickBuildingOverviewFloorManageTargetZones}
          />
          <PermissionRoute
            permission="viewpoints_tables.read_viewpoints_point"
            path={PATH_STRINGS.adminManageTargetZonesFloor}
            component={
              floorLoading || floorIdle || groupsLoading
                ? LoadingIndicator
                : ManageTargetZones
            }
          />
          <PermissionRoute
            exact
            permission="project_tables.read_equipment"
            path={[
              PATH_STRINGS.adminManageEquipment,
              PATH_STRINGS.adminManageEquipmentFloor,
            ]}
            component={ManageEquipment}
            hoverFloor={hoverFloor}
            setHoverFloor={setHoverFloor}
            floorLoading={floorLoading}
          />
          <PermissionRoute
            permission="project_tables.read_project"
            path={PATH_STRINGS.adminManageUsers}
            component={ManageUsers}
          />
          <PermissionRoute
            permission="project_tables.admin_zone_access"
            path={PATH_STRINGS.adminScanHealth}
            component={WrappedManageScanHealth}
          />
          <Route exact path={PATH_STRINGS.adminManageReview}>
            {isSuperUser ? (
              <ReviewTaskList variant="admin" />
            ) : (
              <Redirect to={generateAdminProjectPath(urlProjectId)} />
            )}
          </Route>
          <Route exact path={PATH_STRINGS.adminManageReviewAi}>
            {isSuperUser ? (
              <ReviewAiBatchReviewPage />
            ) : (
              <Redirect to={generateAdminProjectPath(urlProjectId)} />
            )}
          </Route>
          <PermissionRoute
            permission="scheduler_tables.read_scheduler_schedule"
            path={PATH_STRINGS.adminProject}
            component={ManageSchedules}
            floorLoading={floorLoading || floorIdle}
            floors={buildingData.floors}
          />
        </Switch>
      </AdminContentArea>
    </AdminPageRoot>
  )
}

const WrappedManagePoints = () => {
  return (
    <ManagePointsProvider>
      <ManagePoints />
    </ManagePointsProvider>
  )
}

const WrappedManageScanHealth = () => (
  <ScanHealthPageFrame>
    <ManageScanHealth />
  </ScanHealthPageFrame>
)

const AdminPageRoot = styled.div<{ $isScanHealth: boolean }>`
  box-sizing: border-box;

  ${props =>
    props.$isScanHealth &&
    `
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `}
`

const AdminContentArea = styled.div<{ $isScanHealth: boolean }>`
  ${props =>
    props.$isScanHealth &&
    `
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    & > * {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  `}
`

const ScanHealthPageFrame = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
`
